import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { ModuleKey, Permission } from '@pointage/shared';
import { AuthGuard } from './components/AuthGuard.js';
import { AppShell } from './components/AppShell.js';
import { useMe } from './api/hooks.js';
import { EspaceEmployePage } from './pages/EspaceEmploye.js';
import { DocumentsAdminPage } from './pages/DocumentsAdmin.js';
import { ToastContainer } from './components/ToastContainer.js';
import { LoginPage } from './pages/Login.js';
import { DashboardPage } from './pages/Dashboard.js';
import { DailyEntryPage } from './pages/DailyEntry.js';
import { MonthlyGridPage } from './pages/MonthlyGrid.js';
import { InspectPage } from './pages/Inspect.js';
import { CongesPage } from './pages/Conges.js';
import { ContratsPage } from './pages/Contrats.js';
import { RecuperationPage } from './pages/Recuperation.js';
import { EmployeesPage } from './pages/Employees.js';
import { OrganigrammePage } from './pages/Organigramme.js';
import { ReportsPage } from './pages/Reports.js';
import { SettingsPage } from './pages/Settings.js';
import { ActivityPage } from './pages/Activity.js';
import { SalairesPage } from './pages/Salaires.js';
import { EvaluationsPage } from './pages/Evaluations.js';
import { RecrutementPage } from './pages/Recrutement.js';
import { BureauPage } from './pages/Bureau.js';

// React Router 7 in Data Mode (createBrowserRouter + RouterProvider).
// Phase D wired the last four pages (Employees / Reports / Settings /
// Activity); every route now points at a real implementation.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  },
});

// Espace employé: EMPLOYEE accounts never see the back-office shell —
// any admin route redirects them to /espace (the server enforces the
// same boundary via requireStaff on every staff API mount).
// Manager recruteur (RECRUTEUR) : le module Recrutement seul — toute
// autre route le renvoie sur /recrutement (le serveur refuse le reste).
function StaffOnly({ children }: { children: ReactNode }) {
  const me = useMe();
  const { pathname } = useLocation();
  if (me.data?.user.role === 'EMPLOYEE') return <Navigate to="/espace" replace />;
  if (me.data?.user.role === 'RECRUTEUR' && pathname !== '/recrutement') return <Navigate to="/recrutement" replace />;
  return <>{children}</>;
}

// Permission/module route guard. AuthGuard has already resolved the
// session by the time children render; out-of-scope destinations fall
// back to the dashboard (mirrors the server's 403).
function RequireAccess({ perm, module, children }: { perm?: Permission; module?: ModuleKey; children: ReactNode }) {
  const me = useMe();
  if (!me.data) return null;
  const permOk = !perm || me.data.permissions.includes(perm);
  const moduleOk = !module || !me.data.modules || me.data.modules[module];
  if (!permOk || !moduleOk) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function ProtectedShell() {
  return (
    <AuthGuard>
      <StaffOnly>
        <AppShell />
      </StaffOnly>
    </AuthGuard>
  );
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/espace',
    element: (
      <AuthGuard>
        <EspaceEmployePage />
      </AuthGuard>
    ),
  },
  {
    element: <ProtectedShell />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'bureau', element: <RequireAccess perm="bureau.view"><BureauPage /></RequireAccess> },
      { path: 'entry', element: <DailyEntryPage /> },
      { path: 'monthly', element: <MonthlyGridPage /> },
      { path: 'inspect', element: <InspectPage /> },
      { path: 'balances', element: <CongesPage /> },
      { path: 'recup', element: <RecuperationPage /> },
      { path: 'contrats', element: <RequireAccess module="contrats"><ContratsPage /></RequireAccess> },
      { path: 'documents', element: <RequireAccess module="documents"><DocumentsAdminPage /></RequireAccess> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'employees', element: <EmployeesPage /> },
      { path: 'organigramme', element: <OrganigrammePage /> },
      { path: 'salaires', element: <RequireAccess perm="payroll.view" module="payroll"><SalairesPage /></RequireAccess> },
      { path: 'evaluations', element: <RequireAccess perm="evaluations.view" module="evaluations"><EvaluationsPage /></RequireAccess> },
      { path: 'recrutement', element: <RequireAccess perm="recrutement.view" module="recrutement"><RecrutementPage /></RequireAccess> },
      { path: 'activity', element: <RequireAccess perm="activity.view"><ActivityPage /></RequireAccess> },
      { path: 'settings', element: <RequireAccess perm="settings.edit"><SettingsPage /></RequireAccess> },
      { path: 'settings/:topic', element: <RequireAccess perm="settings.edit"><SettingsPage /></RequireAccess> },
    ],
  },
  // Anything unmatched → dashboard (AuthGuard kicks in there if
  // the user isn't signed in).
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ToastContainer />
    </QueryClientProvider>
  );
}

// Unused but kept for symmetry with the AppShell layout — guards
// that need a one-off Outlet wrapper for test setups.
export const TestOutletProbe = Outlet;
